import {
  DatePicker,
  Field,
  FieldDescription,
  FieldError,
  HeaderText,
  Input,
} from "@/components/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";
import { SubmitCancelButtons } from "../../components/SubmitCancelButtons";
import type { VitalFormData } from "../config/vitalsTypes";

interface VitalFormProps {
  type: string;
  title: string;
  description: string;
  schema: z.ZodSchema<VitalFormData>;
  onSubmit(values: VitalFormData): void;
  onCancel(): void;
  isLoading?: boolean;
  defaultValues?: VitalFormData;
}

export const VitalForm = ({
  type,
  title,
  description,
  schema,
  onSubmit,
  onCancel,
  isLoading,
  defaultValues,
}: VitalFormProps) => {
  const form = useForm<VitalFormData>({
    resolver: zodResolver(schema),
    defaultValues: defaultValues,
  });

  return (
    <div className="flex flex-col gap-2 px-3 py-2">
      <HeaderText size="sm">{title}</HeaderText>
      <form
        data-testid={`${type}-form`}
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-2"
      >
        <div className="flex flex-col gap-3 w-full">
          <Controller
            control={form.control}
            name="value"
            render={({ field, fieldState }) => (
              <Field className="w-full" data-invalid={fieldState.invalid}>
                <Input
                  {...field}
                  data-testid={`${type}-input`}
                  autoFocus
                  aria-invalid={fieldState.invalid}
                />
                <FieldDescription>{description}</FieldDescription>
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
          <Controller
            control={form.control}
            name="effectiveDateTime"
            render={({ field, fieldState }) => (
              <Field className="w-full" data-invalid={fieldState.invalid}>
                <DatePicker
                  date={field.value ? new Date(field.value) : new Date()}
                  setDate={(date) => {
                    field.onChange(date.toUTCString());
                  }}
                  dateFormat="MM-dd-yyyy"
                  closeOnSelect={true}
                  label=""
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </div>
        <div className="flex justify-end -mt-1">
          <SubmitCancelButtons
            onCancel={onCancel}
            isSaving={isLoading ?? false}
          />
        </div>
      </form>
    </div>
  );
};
